import React from "react";
import {
    Check,
    X,
    ArrowRight,
    Database,
    AlertTriangle,
    HandshakeIcon,
    Mail,
} from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";
import { getComparison, COMPARISONS } from "../lib/comparisons";
import { getProduct } from "../lib/products";

const Yes = () => <span style={{ color: "var(--accent)", display: "inline-flex" }}><Check size={18} /></span>;
const No = () => <span style={{ color: "var(--text-dim)", display: "inline-flex" }}><X size={18} /></span>;
const Partial = ({ text }) => (
    <span style={{ color: "#f0883e", fontSize: "0.78rem", fontWeight: 600 }}>
        {text || "Partial"}
    </span>
);

const cell = (v) => {
    if (v === true) return <Yes />;
    if (v === false) return <No />;
    if (v === "partial" || v === "Partial") return <Partial />;
    return <span style={{ fontWeight: 500, color: "var(--text)", fontSize: "0.86rem" }}>{v}</span>;
};

const NotFound = () => (
    <section className="section" data-testid="compare-detail-not-found">
        <div className="container" style={{ maxWidth: 720, textAlign: "center" }}>
            <h1 className="h1">No comparison here.</h1>
            <p className="muted" style={{ marginTop: 16 }}>
                We've written detailed head-to-head comparisons for these ERPs:
            </p>
            <ul style={{ listStyle: "none", padding: 0, marginTop: 24, display: "grid", gap: 10 }}>
                {COMPARISONS.map((c) => (
                    <li key={c.slug}>
                        <a href={`/compare/${c.slug}`} className="link-arrow" data-testid={`compare-detail-fallback-link-${c.slug}`}>
                            {c.headline} →
                        </a>
                    </li>
                ))}
            </ul>
            <div style={{ marginTop: 32 }}>
                <a href="/compare" className="btn btn-secondary" data-testid="compare-detail-back">
                    Back to all comparisons
                </a>
            </div>
        </div>
    </section>
);

export const CompareDetailPage = ({ slug }) => {
    const c = getComparison(slug);
    if (!c) return <NotFound />;

    const products = (c.topProducts || []).map((s) => getProduct(s)).filter(Boolean);

    return (
        <section className="section" data-testid={`compare-detail-page-${c.slug}`}>
            <div className="container">
                {/* Breadcrumb */}
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 24,
                        fontSize: "0.84rem",
                        color: "var(--text-muted)",
                    }}
                    data-testid="compare-detail-breadcrumb"
                >
                    <a href="/compare" style={{ color: "var(--text-muted)" }}>Compare</a>
                    <span>/</span>
                    <span style={{ color: "var(--text)" }}>{c.name}</span>
                </div>

                {/* Hero */}
                <div className="section-head" style={{ textAlign: "left" }}>
                    <span className="eyebrow">{c.category}</span>
                    <h1
                        className="h1"
                        style={{ marginTop: 18, textAlign: "left" }}
                        data-testid={`compare-detail-h1-${c.slug}`}
                    >
                        {c.headline}
                    </h1>
                    <p
                        style={{
                            color: "var(--accent)",
                            fontSize: "1.15rem",
                            fontWeight: 600,
                            marginTop: 16,
                            textAlign: "left",
                            maxWidth: 760,
                        }}
                        data-testid={`compare-detail-tagline-${c.slug}`}
                    >
                        {c.tagline}
                    </p>
                    <p
                        style={{
                            fontSize: "1.02rem",
                            lineHeight: 1.7,
                            color: "var(--text-muted)",
                            marginTop: 18,
                            textAlign: "left",
                            maxWidth: 800,
                        }}
                    >
                        {c.intro}
                    </p>
                </div>

                {/* Where it fits / where it hurts */}
                <FadeUpGroup>
                    <div
                        className="fade-up"
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr",
                            gap: 18,
                            marginTop: 56,
                        }}
                    >
                        <div
                            className="card"
                            style={{ padding: "26px 28px", borderLeft: "3px solid var(--accent)" }}
                            data-testid="compare-detail-where-fits"
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    marginBottom: 10,
                                }}
                            >
                                <span
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 8,
                                        background: "var(--accent-soft)",
                                        color: "var(--accent)",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <Database size={15} />
                                </span>
                                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", fontSize: "1.05rem" }}>
                                    Where {c.name} fits
                                </h3>
                            </div>
                            <p className="muted" style={{ margin: 0, fontSize: "0.96rem", lineHeight: 1.7 }}>
                                {c.whereItFits}
                            </p>
                        </div>

                        <div
                            className="card"
                            style={{ padding: "26px 28px", borderLeft: "3px solid #f0883e" }}
                            data-testid="compare-detail-where-hurts"
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    marginBottom: 14,
                                }}
                            >
                                <span
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 8,
                                        background: "rgba(240, 136, 62, 0.12)",
                                        color: "#f0883e",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AlertTriangle size={15} />
                                </span>
                                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", fontSize: "1.05rem" }}>
                                    Where {c.name} hurts a growing operator
                                </h3>
                            </div>
                            <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
                                {c.whereItHurts.map((line) => (
                                    <li
                                        key={line}
                                        style={{
                                            display: "flex",
                                            gap: 10,
                                            alignItems: "flex-start",
                                            color: "var(--text-muted)",
                                            fontSize: "0.94rem",
                                            lineHeight: 1.55,
                                        }}
                                    >
                                        <X size={15} style={{ color: "#f0883e", flexShrink: 0, marginTop: 4 }} />
                                        <span>{line}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <div
                            className="card"
                            style={{ padding: "26px 28px", borderLeft: "3px solid var(--accent)", background: "var(--accent-soft)" }}
                            data-testid="compare-detail-how-we-fit"
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                    marginBottom: 10,
                                }}
                            >
                                <span
                                    style={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: 8,
                                        background: "var(--bg)",
                                        color: "var(--accent)",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <HandshakeIcon size={15} />
                                </span>
                                <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--text)", fontSize: "1.05rem" }}>
                                    How Scitus.ai fits alongside {c.name}
                                </h3>
                            </div>
                            <p style={{ margin: 0, fontSize: "0.96rem", lineHeight: 1.7, color: "var(--text)" }}>
                                {c.howWeFit}
                            </p>
                        </div>
                    </div>
                </FadeUpGroup>

                {/* Feature head-to-head */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 72 }}>
                        <h2
                            className="h2"
                            style={{ textAlign: "center", margin: "0 0 28px 0" }}
                            data-testid={`compare-detail-feature-heading-${c.slug}`}
                        >
                            Feature-by-feature
                        </h2>
                        <div className="compare-table" data-testid={`compare-detail-table-${c.slug}`}>
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{ width: "44%" }}>Capability</th>
                                        <th style={{ width: "28%" }}>
                                            {c.name}
                                            <div
                                                style={{
                                                    fontWeight: 400,
                                                    fontSize: "0.72rem",
                                                    color: "var(--text-dim)",
                                                    marginTop: 4,
                                                    letterSpacing: 0,
                                                }}
                                            >
                                                {c.sub}
                                            </div>
                                        </th>
                                        <th style={{ width: "28%", color: "var(--accent)" }}>
                                            Scitus.ai
                                            <div
                                                style={{
                                                    fontWeight: 400,
                                                    fontSize: "0.72rem",
                                                    color: "var(--text-dim)",
                                                    marginTop: 4,
                                                    letterSpacing: 0,
                                                }}
                                            >
                                                Standalone products, one platform
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {c.feature.map((row) => (
                                        <tr key={row.f}>
                                            <td>{row.f}</td>
                                            <td>{cell(row.their)}</td>
                                            <td>{cell(row.ours)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </FadeUpGroup>

                {/* Our honest position */}
                <FadeUpGroup>
                    <div
                        className="fade-up"
                        style={{
                            marginTop: 72,
                            padding: "32px 36px",
                            background: "var(--surface)",
                            border: "1px solid var(--border)",
                            borderRadius: 16,
                        }}
                        data-testid="compare-detail-honest"
                    >
                        <span
                            className="eyebrow"
                            style={{
                                display: "inline-block",
                                marginBottom: 12,
                                color: "var(--accent)",
                            }}
                        >
                            Our honest position
                        </span>
                        <p
                            style={{
                                margin: 0,
                                fontSize: "1.05rem",
                                lineHeight: 1.7,
                                color: "var(--text)",
                                fontFamily: "var(--font-display)",
                                fontWeight: 500,
                                fontStyle: "italic",
                            }}
                        >
                            "{c.honest}"
                        </p>
                    </div>
                </FadeUpGroup>

                {/* Top products for this scenario */}
                {products.length > 0 && (
                    <FadeUpGroup>
                        <div className="fade-up" style={{ marginTop: 72 }}>
                            <h2 className="h2" style={{ textAlign: "center", margin: "0 0 28px 0" }}>
                                Where most {c.name} customers start
                            </h2>
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                    gap: 16,
                                }}
                                data-testid={`compare-detail-products-${c.slug}`}
                            >
                                {products.map((p) => (
                                    <a
                                        key={p.slug}
                                        href={`/products/${p.slug}`}
                                        className="card"
                                        style={{
                                            padding: "20px 22px",
                                            textDecoration: "none",
                                            display: "block",
                                        }}
                                        data-testid={`compare-detail-product-${c.slug}-${p.slug}`}
                                    >
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                marginBottom: 8,
                                            }}
                                        >
                                            <img
                                                src={p.logo.src}
                                                alt={`${p.name} logo — ${p.sub}`}
                                                width="24"
                                                height="24"
                                                loading="lazy"
                                                decoding="async"
                                            />
                                            <span
                                                style={{
                                                    fontFamily: "var(--font-display)",
                                                    fontWeight: 700,
                                                    color: "var(--text)",
                                                    fontSize: "1rem",
                                                }}
                                            >
                                                {p.name}
                                            </span>
                                        </div>
                                        <p className="muted" style={{ margin: 0, fontSize: "0.86rem", lineHeight: 1.5 }}>
                                            {p.sub}
                                        </p>
                                        <span
                                            className="link-arrow"
                                            style={{ marginTop: 14, display: "inline-flex", fontSize: "0.83rem" }}
                                        >
                                            Explore →
                                        </span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    </FadeUpGroup>
                )}

                {/* FAQ */}
                {c.faq && c.faq.length > 0 && (
                    <FadeUpGroup>
                        <div className="fade-up" style={{ marginTop: 72 }}>
                            <h2 className="h2" style={{ textAlign: "center", margin: "0 0 28px 0" }}>
                                Frequently asked
                            </h2>
                            <div style={{ display: "grid", gap: 12, maxWidth: 820, margin: "0 auto" }} data-testid={`compare-detail-faq-${c.slug}`}>
                                {c.faq.map((f, i) => (
                                    <details
                                        key={f.q}
                                        className="card"
                                        style={{ padding: "18px 22px" }}
                                        data-testid={`compare-detail-faq-${c.slug}-${i}`}
                                    >
                                        <summary
                                            style={{
                                                cursor: "pointer",
                                                color: "var(--text)",
                                                fontFamily: "var(--font-display)",
                                                fontWeight: 600,
                                                fontSize: "1rem",
                                                listStyle: "none",
                                            }}
                                        >
                                            {f.q}
                                        </summary>
                                        <p className="muted" style={{ margin: "12px 0 0 0", fontSize: "0.93rem", lineHeight: 1.7 }}>
                                            {f.a}
                                        </p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    </FadeUpGroup>
                )}

                {/* Other comparisons */}
                <FadeUpGroup>
                    <div className="fade-up" style={{ marginTop: 72 }}>
                        <h2 className="h2" style={{ textAlign: "center", margin: "0 0 24px 0" }}>
                            Other head-to-heads
                        </h2>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                                gap: 14,
                            }}
                            data-testid={`compare-detail-related-${c.slug}`}
                        >
                            {COMPARISONS.filter((x) => x.slug !== c.slug).map((x) => (
                                <a
                                    key={x.slug}
                                    href={`/compare/${x.slug}`}
                                    className="card"
                                    style={{ padding: "20px 22px", textDecoration: "none", display: "block" }}
                                    data-testid={`compare-detail-related-link-${x.slug}`}
                                >
                                    <div
                                        style={{
                                            fontFamily: "var(--font-display)",
                                            fontWeight: 700,
                                            color: "var(--text)",
                                            fontSize: "1rem",
                                            marginBottom: 6,
                                        }}
                                    >
                                        Scitus.ai vs. {x.name}
                                    </div>
                                    <span className="muted" style={{ fontSize: "0.86rem" }}>{x.tagline}</span>
                                </a>
                            ))}
                        </div>
                    </div>
                </FadeUpGroup>

                {/* CTA */}
                <div className="cta-banner" style={{ marginTop: 80 }}>
                    <div>
                        <h2 className="h2">{c.cta}.</h2>
                        <p className="lead mt-4">
                            30-minute working session with an operator on the Scitus team. Bring your current{" "}
                            {c.name} stack and the workflow that's holding you back. We'll show you what would move,
                            what stays, and how the integration with {c.name} runs in production.
                        </p>
                    </div>
                    <a href="/contact" className="btn btn-primary btn-lg" data-testid={`compare-detail-cta-${c.slug}`}>
                        Talk to a Scitus operator <ArrowRight size={18} />
                    </a>
                </div>

                {/* Secondary email CTA */}
                <p
                    className="muted"
                    style={{ textAlign: "center", marginTop: 24, fontSize: "0.88rem" }}
                >
                    Or write to{" "}
                    <a href="mailto:hello@scitus.ai" style={{ color: "var(--accent)" }}>
                        <Mail size={13} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
                        hello@scitus.ai
                    </a>
                </p>
            </div>
        </section>
    );
};

export default CompareDetailPage;
